import { DateTime } from 'luxon'
import { I18n } from 'aws-amplify'

const MINUTELY = 'minutely'
const HOURLY = 'hourly'
const DAILY = 'daily'
const WEEKLY = 'weekly'
const MONTHLY = 'monthly'

const DATE_RANGES = [
  'last24Hours',
  'last7Days',
  'last30Days',
  'last90Days',
  'lastYear',
  'customRange'
]

const INTERVALS = {
  [MINUTELY]: {
    label: () => I18n.get('Minutely'),
    value: MINUTELY,
    query: '1m',
    dateFormat: 'HH:mm',
    hierarchy: 1
  },
  [HOURLY]: {
    label: () => I18n.get('Hourly'),
    value: HOURLY,
    query: '1h',
    dateFormat: 'HH:mm',
    hierarchy: 2
  },
  [DAILY]: {
    label: () => I18n.get('Daily'),
    value: DAILY,
    query: '1d',
    dateFormat: 'dd/MM/yy',
    hierarchy: 3
  },
  [WEEKLY]: {
    label: () => I18n.get('Weekly'),
    value: WEEKLY,
    query: '7d',
    dateFormat: 'dd/MM/yy',
    hierarchy: 4
  },
  [MONTHLY]: {
    label: () => I18n.get('Monthly'),
    value: MONTHLY,
    query: '30d',
    dateFormat: 'dd/MM/yy',
    hierarchy: 5
  }
}

export const getDefaultDateFilters = () => {
  const now = DateTime.now().plus({ days: 1 })
  const lastWeek = now.minus({ days: 7 })

  return {
    dateRange: 'last7Days',
    fromDate: lastWeek.toMillis(),
    toDate: now.toMillis(),
    timeInterval: HOURLY
  }
}

export const getAvailableDateRanges = () => {
  return DATE_RANGES
}

export const getDateRangeOptions = option => {
  if (!option || option === 'customRange') {
    return {}
  }

  const now = DateTime.now()

  switch (option) {
    case 'last24Hours':
      return {
        fromDate: now.minus({ hours: 24 }).toMillis(),
        toDate: now.toMillis()
      }
    case 'last7Days':
      return {
        fromDate: now.minus({ days: 7 }).toMillis(),
        toDate: now.toMillis()
      }
    case 'last30Days':
      return {
        fromDate: now.minus({ days: 30 }).toMillis(),
        toDate: now.toMillis()
      }
    case 'last90Days':
      return {
        fromDate: now.minus({ days: 90 }).toMillis(),
        toDate: now.toMillis()
      }
    case 'lastYear':
      return {
        fromDate: now.minus({ days: 365 }).toMillis(),
        toDate: now.toMillis()
      }
    default:
      return {}
  }
}

export const getRecommendedTimeInterval = (fromDate, toDate) => {
  const diffInDays = (toDate - fromDate) / (1000 * 60 * 60 * 24)

  if (diffInDays <= 1) {
    return MINUTELY
  } else if (diffInDays <= 7) {
    return HOURLY
  } else if (diffInDays <= 30) {
    return DAILY
  } else if (diffInDays <= 365) {
    return WEEKLY
  } else {
    return MONTHLY
  }
}

const shouldDisableInterval = (interval, referenceInterval) => {
  return (
    INTERVALS[interval].hierarchy + 1 < INTERVALS[referenceInterval].hierarchy
  )
}

export const getIntervalOptions = (recommendedInterval = null) => {
  return Object.keys(INTERVALS).map(key => ({
    value: key,
    label: INTERVALS[key].label(),
    isDisabled: recommendedInterval
      ? shouldDisableInterval(key, recommendedInterval)
      : false
  }))
}

export const getIntervalQuery = intervalKey => {
  return INTERVALS[intervalKey]?.query || '1h'
}

export const millisToTimeZoneMillis = (
  date,
  timeZone,
  offsetTarget = false
) => {
  if (!date) return 0
  const targetOffsetMinutes = DateTime.fromMillis(date).setZone(timeZone).offset
  const browserOffsetMinutes = -new Date().getTimezoneOffset()
  const offsetDifference = targetOffsetMinutes - browserOffsetMinutes
  const adjustedTime =
    date + (offsetTarget ? -1 : 1) * (offsetDifference * 60 * 1000)
  return DateTime.fromMillis(adjustedTime).toJSDate()
}
